import axios from "axios";
import { createHash } from "utils/createHash";

import { db } from "config/firebase";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
} from "firebase/firestore";

import * as geofire from "geofire-common";

async function addLocationFields(gameData: any): Promise<any> {
  // get geocords from zipcode
  try {
    const res = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${gameData.zipcode}&key=AIzaSyDoG4eWlZpyV4bUijIbWZWEgwuw60f6fEE`
    );
    const addressComponents = res.data.results[0].address_components;
    console.log(`addressComponents`, addressComponents);
    const last = addressComponents.length - 1;
    const updatedGameData = { ...gameData };
    updatedGameData.country = addressComponents[last];
    const fetchedState = addressComponents[last - 1];
    console.log(`fetchedState`, fetchedState);
    if (fetchedState !== gameData.state) {
      // TODO: warn user that wrong state is input
      console.log("wrong state");
    }
    const { lat, lng } = res.data.results[0].geometry.location;
    updatedGameData.lat = lat;
    updatedGameData.lng = lng;
    updatedGameData.geohash = geofire.geohashForLocation([lat, lng]);
    return updatedGameData;
  } catch (err) {
    console.log("error getting geocords", err);
    throw err;
  }
}

export const createGame = async ({
  gameData,
  finalCB = () => {},
}: {
  gameData: any;
  finalCB?: () => void;
}): Promise<any> => {
  try {
    // Use the provided game ID (generated by frontend) instead of creating a new one
    const gameId = gameData.id || createHash(Date.now() + gameData.hostId);
    
    // Check for collision - important for private games, but also good for public games
    const existingGameRef = doc(db, "games", gameId);
    const existingGameSnap = await getDoc(existingGameRef);
    
    if (existingGameSnap.exists()) {
      if (gameData.access === "Private" && gameData.accesscode) {
        return {
          status: "ERROR",
          message: `Access code "${gameData.accesscode}" is already in use. Please choose a different access code.`,
        };
      } else {
        // For public games, this is very unlikely but handle gracefully
        return {
          status: "ERROR", 
          message: "A game with this configuration already exists. Please try again or use a different game name.",
        };
      }
    }
    
    const updatedGameData = await addLocationFields(gameData);
    const finalGameData = {
      ...updatedGameData,
      id: gameId,
      waitlist: {},
      currentPlayers: {},
      playerlist: {},
    };
    await setDoc(doc(db, "games", gameId), finalGameData);
    return {
      status: "SUCCESS",
      message: "Game Created Successfully",
    };
  } catch (err) {
    console.log("Error Creating Game: ", err);
    return { status: "ERROR", message: "Error creating game" };
  } finally {
    finalCB();
  }
};

export const editGame = async ({
  gameData,
  finalCB,
}: {
  gameData: any;
  finalCB: () => void;
}): Promise<any> => {
  try {
    const gameId = gameData.id;
    await setDoc(doc(db, "games", gameId), gameData, { merge: true });
    return {
      status: "SUCCESS",
      message: "Game Details Updated Successfully",
    };
  } catch (err) {
    console.log("Error Updating Game Details: ", err);
    return {
      status: "ERROR",
      message: `Error Updating Game Details`,
    };
  } finally {
    finalCB();
  }
};

const deleteAllRequests = async (gameId: string): Promise<void> => {
  try {
    const q = query(collection(db, "requests"), where("gameId", "==", gameId));
    const querySnapshot = await getDocs(q);

    const deletePromises = querySnapshot.docs.map((docSnapshot) =>
      deleteDoc(docSnapshot.ref)
    );

    await Promise.all(deletePromises);
  } catch (err) {
    console.log(err);
  }
};

export const deleteGame = async (gameId: string): Promise<void> => {
  await deleteDoc(doc(db, "games", gameId));
  await deleteAllRequests(gameId);
};

const startGame = async (gameId: string): Promise<any> => {
  try {
    const startedAt = Date.now();
    const sessionId = createHash(gameId + startedAt);

    await updateDoc(doc(db, "games", gameId), { startedAt, sessionId });
    return { startedAt, sessionId };
  } catch (err) {
    console.log("Error updating game start time", err);
    return err;
  }
};

const changeGameStatus = async (
  gameId: string,
  status: string
): Promise<any> => {
  try {
    await updateDoc(doc(db, "g,ames", gameId), {
      status,
      timeStamp: Date.now(),
    });
    return "success";
  } catch (err) {
    return err;
  }
};

const endGame = async (gameId: string): Promise<any> => {
  try {
    const prom1 = updateDoc(doc(db, "games", gameId), {
      waitlist: {},
      currentPlayers: {},
      playerlist: {},
    });
    const prom2 = deleteAllRequests(gameId);

    await Promise.all([prom1, prom2]);
    return "success";
  } catch (err) {
    return err;
  }
};

export default {
  createGame,
  editGame,
  deleteGame,
  startGame,
  changeGameStatus,
  endGame,
};
